import { useState, useMemo } from "react";
import { format } from "date-fns";
import { CalendarRange, ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface DateRangePreset {
  key: string;
  label: string;
  days: number;
  /**
   * AUT-420: a preset the caller has, but that this context cannot honour — the keyword chart
   * offers 7/30/60/90 and greys out any window wider than the history it actually holds. Shown
   * rather than filtered out, so the option's existence stays visible.
   */
  disabled?: boolean;
}

export const DEFAULT_RANGE_PRESETS: DateRangePreset[] = [
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "14", label: "Last 14 days", days: 14 },
  { key: "28", label: "Last 28 days", days: 28 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
];

export interface DateRangePickerProps {
  selectedDays: number;
  onDaysChange: (days: number) => void;
  /** Custom range value. Only used when allowCustom is true. */
  customDateRange?: { from: Date | undefined; to: Date | undefined };
  onCustomDateChange?: (range: { from: Date | undefined; to: Date | undefined }) => void;
  /** Variation: enable the "Custom" option (shown last, separated by a divider). */
  allowCustom?: boolean;
  presets?: DateRangePreset[];
  className?: string;
  /**
   * AUT-420: what the control *is*, for assistive tech. The trigger's own text is the current
   * window ("Last 30 days"), which says nothing about what choosing it changes -- the `<select>`
   * these replaced carried an `sr-only` "Date range" label that was lost in the conversion.
   * Rendered as an `sr-only` span rather than `aria-label`, which would *replace* the name and
   * take the current window with it: the name reads "Date range: Last 30 days", keeping both.
   */
  ariaLabel?: string;
}

/**
 * Canonical date RANGE selector. Uses the CalendarRange icon + caret.
 * For single dates use <SingleDatePicker /> instead.
 */
export function DateRangePicker({
  selectedDays,
  onDaysChange,
  customDateRange = { from: undefined, to: undefined },
  onCustomDateChange,
  allowCustom = true,
  presets = DEFAULT_RANGE_PRESETS,
  className,
  ariaLabel,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  // Derived from props, not held in state. The prototype seeded `activePreset` with `useState`,
  // which never resyncs -- fine in a style-guide demo, wrong here: `/rank-tracker` resets the
  // window to its default when the brand changes, and a state-held preset would keep showing the
  // previous window's label ("Last 90 days" over 30-day data). Pinned by RankTracker.test.tsx
  // § "resets ... the window, when selectedBrandId changes". A custom range wins when one is
  // applied, so a custom span that happens to equal a preset still reads as its dates.
  const hasCustomRange = allowCustom && Boolean(customDateRange.from && customDateRange.to);
  const activePreset = hasCustomRange
    ? "custom"
    : (presets.find((p) => p.days === selectedDays)?.key ?? (allowCustom ? "custom" : presets[0]?.key));
  const [pendingRange, setPendingRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: customDateRange.from,
    to: customDateRange.to,
  });

  const isCustom = allowCustom && activePreset === "custom";

  const displayLabel = useMemo(() => {
    if (isCustom && customDateRange.from && customDateRange.to) {
      return `${format(customDateRange.from, "MMM d")} – ${format(customDateRange.to, "MMM d, yyyy")}`;
    }
    const preset = presets.find((p) => p.key === activePreset);
    return preset ? preset.label : `Last ${selectedDays} days`;
  }, [activePreset, selectedDays, customDateRange, isCustom, presets]);

  const handlePresetClick = (preset: DateRangePreset) => {
    if (preset.disabled) return;

    setShowCalendar(false);
    onDaysChange(preset.days);
    onCustomDateChange?.({ from: undefined, to: undefined });
    setPendingRange({ from: undefined, to: undefined });
    setOpen(false);
  };

  const handleApply = () => {
    if (pendingRange.from && pendingRange.to) {
      onCustomDateChange?.(pendingRange);
      const diffTime = Math.abs(pendingRange.to.getTime() - pendingRange.from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      onDaysChange(diffDays);
      setOpen(false);
      setShowCalendar(false);
    }
  };

  const handleCancel = () => {
    setPendingRange({ from: customDateRange.from, to: customDateRange.to });
    setShowCalendar(false);
    setOpen(false);
  };

  const optionClass = (active: boolean) =>
    cn(
      "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors rounded-md",
      active ? "text-foreground font-medium" : "text-foreground hover:bg-primary/15"
    );

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setShowCalendar(false);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-9 justify-start text-left font-normal gap-2", className)}
        >
          {ariaLabel && <span className="sr-only">{ariaLabel}: </span>}
          <CalendarRange className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm">{displayLabel}</span>
          <ChevronDown className="ml-1 h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-card border border-border shadow-lg z-[70]"
        align="end"
        sideOffset={8}
      >
        <div className="flex">
          {/* Options list */}
          <div className={cn("w-48 p-1.5", showCalendar && "border-r border-border")}>
            {presets.map((preset) => (
              <button
                key={preset.key}
                onClick={() => handlePresetClick(preset)}
                disabled={preset.disabled}
                className={cn(
                  optionClass(activePreset === preset.key && !showCalendar),
                  preset.disabled && "cursor-not-allowed opacity-40 hover:bg-transparent",
                )}
              >
                <Check
                  className={cn(
                    "h-4 w-4 shrink-0",
                    activePreset === preset.key && !showCalendar ? "opacity-100" : "opacity-0"
                  )}
                />
                {preset.label}
              </button>
            ))}

            {allowCustom && (
              <>
                <div className="h-px bg-border my-1.5" />
                <button
                  onClick={() => setShowCalendar(true)}
                  className={optionClass(isCustom || showCalendar)}
                >
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0",
                      isCustom || showCalendar ? "opacity-100" : "opacity-0"
                    )}
                  />
                  Custom
                </button>
              </>
            )}
          </div>

          {/* Calendar */}
          {allowCustom && showCalendar && (
            <div className="flex flex-col">
              <div className="flex items-center gap-2 px-4 pt-3 pb-2">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground font-medium">Start date</label>
                  <div className="border border-border rounded-md px-3 py-1.5 text-sm mt-0.5">
                    {pendingRange.from ? format(pendingRange.from, "MMM d, yyyy") : "—"}
                  </div>
                </div>
                <span className="text-muted-foreground mt-4">–</span>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground font-medium">End date</label>
                  <div className="border border-border rounded-md px-3 py-1.5 text-sm mt-0.5">
                    {pendingRange.to ? format(pendingRange.to, "MMM d, yyyy") : "—"}
                  </div>
                </div>
              </div>

              <Calendar
                mode="range"
                selected={{ from: pendingRange.from, to: pendingRange.to }}
                onSelect={(range) => setPendingRange({ from: range?.from, to: range?.to })}
                numberOfMonths={1}
                className="p-3 pointer-events-auto"
                disabled={(date) => date > new Date()}
              />

              <div className="flex items-center justify-end gap-3 px-4 pb-3 pt-1 border-t border-border">
                <Button variant="ghost" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={!pendingRange.from || !pendingRange.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
